import * as React from 'react'

import { GripVerticalIcon, Trash2Icon } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Kbd } from '~/client/src/shared/components/shadcn-ui/kbd'
import {
  Tooltip,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { ArrowheadPosition, MarkupKind } from './MarkupDrawController'
import type { IStylePreview } from './MarkupEditorStore'
import { MarkupTooltipContent as TooltipContent } from './MarkupPortalContainer'
import {
  ArrowheadSegment,
  BorderSegment,
  BORDER_MAX,
  ColorSegment,
  DIVIDER,
  FillSegment,
  LINE_MAX,
  WeightSegment,
} from './markupSegments'
import { ArrowheadType, FillStyle, StrokeStyle } from './markupTools'

interface IProps {
  kind: MarkupKind
  color: string
  strokeWidth: number
  strokeStyle: StrokeStyle
  fillColor: string
  fillStyle: FillStyle
  arrowheadStart: ArrowheadType
  arrowheadEnd: ArrowheadType
  onColorChange: (color: string) => void
  onStrokeWidthChange: (width: number) => void
  onStrokeStyleChange: (style: StrokeStyle) => void
  onFillColorChange: (color: string) => void
  onFillStyleChange: (style: FillStyle) => void
  onArrowheadChange: (position: ArrowheadPosition, type: ArrowheadType) => void
  // Omitted on mobile (the toolbar is pinned above the markup, not draggable) → the
  // drag grip is hidden.
  onMovePointerDown?: (e: React.PointerEvent) => void
  onDelete: () => void
  // Touch: drop the keyboard "Del" keycap from the delete tooltip.
  isMobileEnv?: boolean
  // Mobile only: live-preview commit controls for the picker bottom sheets.
  stylePreview?: IStylePreview
}

// Rich text (T) is intentionally absent until text-in-shape rendering lands.
const MarkupContextToolbar: React.FC<IProps> = ({
  kind,
  color,
  strokeWidth,
  strokeStyle,
  fillColor,
  fillStyle,
  arrowheadStart,
  arrowheadEnd,
  onColorChange,
  onStrokeWidthChange,
  onStrokeStyleChange,
  onFillColorChange,
  onFillStyleChange,
  onArrowheadChange,
  onMovePointerDown,
  onDelete,
  isMobileEnv,
  stylePreview,
}) => {
  const isShape = kind === 'shape'
  const isArrow = kind === 'arrow'
  const hasThickness = kind === 'freehand' || kind === 'highlighter'

  return (
    <div className="tw:flex tw:items-stretch tw:rounded-lg tw:bg-popover tw:shadow-lg">
      {/* Drag handle: lets the user slide the toolbar off the item it's covering.
        Hidden on mobile, where the toolbar is pinned above the markup. */}
      {onMovePointerDown && (
        <span
          aria-label={Localization.translator.moveToolbar}
          onPointerDown={onMovePointerDown}
          className={`tw:flex tw:cursor-grab tw:items-center tw:justify-center tw:px-1 tw:text-muted-foreground tw:active:cursor-grabbing ${DIVIDER}`}
        >
          <GripVerticalIcon className="tw:size-4" aria-hidden="true" />
        </span>
      )}
      {isShape ? (
        <FillSegment
          color={fillColor}
          fillStyle={fillStyle}
          onColorChange={onFillColorChange}
          onFillStyleChange={onFillStyleChange}
          isMobileEnv={isMobileEnv}
          stylePreview={stylePreview}
        />
      ) : (
        <ColorSegment
          color={color}
          onChange={onColorChange}
          mode={kind === 'highlighter' ? 'wash' : 'opaque'}
          isMobileEnv={isMobileEnv}
          stylePreview={stylePreview}
        />
      )}
      {(isShape || isArrow) && (
        <BorderSegment
          width={strokeWidth}
          strokeStyle={strokeStyle}
          onWidthChange={onStrokeWidthChange}
          onStrokeStyleChange={onStrokeStyleChange}
          color={isShape ? color : undefined}
          onColorChange={isShape ? onColorChange : undefined}
          max={isShape ? BORDER_MAX : LINE_MAX}
          isMobileEnv={isMobileEnv}
          stylePreview={stylePreview}
        />
      )}
      {isArrow && (
        <>
          <ArrowheadSegment
            position="start"
            value={arrowheadStart}
            onChange={onArrowheadChange}
            isMobileEnv={isMobileEnv}
            stylePreview={stylePreview}
          />
          <ArrowheadSegment
            position="end"
            value={arrowheadEnd}
            onChange={onArrowheadChange}
            isMobileEnv={isMobileEnv}
            stylePreview={stylePreview}
          />
        </>
      )}
      {hasThickness && (
        <WeightSegment
          width={strokeWidth}
          onChange={onStrokeWidthChange}
          isMobileEnv={isMobileEnv}
          stylePreview={stylePreview}
        />
      )}
      <div className="tw:flex tw:items-center tw:p-1">
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={Localization.translator.delete}
                onClick={onDelete}
                className="tw:text-destructive tw:hover:text-destructive"
              />
            }
          >
            <Trash2Icon aria-hidden="true" />
          </TooltipTrigger>
          <TooltipContent>
            {Localization.translator.delete}
            {!isMobileEnv && <Kbd>Del</Kbd>}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}

export default MarkupContextToolbar
